/**
 * Task-shaped DTOs that come back from the API.
 *
 * These mirror the Drizzle row shape but as plain JSON: timestamps come
 * across as ISO strings. Components consume `TaskDto` and convert dates
 * lazily where needed (e.g. the Today screen's bucketing logic).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiSend } from './client';

export interface TaskDto {
  id: string;
  workspaceId: string;
  title: string;
  description: string | null;
  status:
    | 'inbox'
    | 'next'
    | 'scheduled'
    | 'waiting'
    | 'delegated'
    | 'blocked'
    | 'someday'
    | 'done';
  priority: 'critical' | 'important' | 'routine' | 'low';
  contextId: string | null;
  projectId: string | null;
  areaId: string | null;
  personId: string | null;
  // Owner: exactly one of ownerId / ownerAgentId is non-null per the
  // tasks_owner_xor_owner_agent CHECK ([[0024]] / [[0028]]).
  ownerId: string | null;
  ownerAgentId: string | null;
  sourceKind: string | null;
  sourceRef: string | null;
  dueAt: string | null;
  scheduledAt: string | null;
  reviewAt: string | null;
  completedAt: string | null;
  archivedAt: string | null;
  waitingFor: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  // Embedded refs from list endpoints — null if no FK or not joined.
  context?: { label: string; color: string; slug: string | null } | null;
  project?: { id: string | null; name: string } | null;
  area?: { id: string | null; name: string } | null;
  person?: { id: string | null; name: string; initials: string; color: string } | null;
  // Discriminated owner shape from list/detail endpoints.
  owner?: { kind: 'user' | 'agent'; id: string; label: string } | null;
}

export interface CountsDto {
  today: number;
  todayOverdue: number;
  inbox: number;
  upcoming: number;
  waiting: number;
  waitingStale: number;
  all: number;
  review: number;
  projects: number;
  areas: number;
  people: number;
}

export function useCounts() {
  return useQuery({
    queryKey: ['tasks', 'counts'],
    queryFn: () => apiGet<CountsDto>('/tasks/counts'),
    staleTime: 30_000,
  });
}

export interface TaskEventDto {
  id: string;
  taskId: string;
  workspaceId: string;
  kind: string;
  actorKind: 'user' | 'agent' | 'system';
  actorUserId: string | null;
  actorAgentKeyId?: string | null;
  actorLabel?: string | null;
  payload: unknown;
  createdAt: string;
}

export interface TaskTagDto {
  id: string;
  name: string;
}

export function useTasksToday() {
  return useQuery({
    queryKey: ['tasks', 'today'],
    queryFn: () => apiGet<{ tasks: TaskDto[] }>('/tasks/today'),
    staleTime: 30_000,
  });
}

export function useTasksCalendar(date: string) {
  return useQuery({
    queryKey: ['tasks', 'calendar', date],
    queryFn: () =>
      apiGet<{
        date: string;
        due: TaskDto[];
        closed: TaskDto[];
        created: TaskDto[];
        updated: TaskDto[];
      }>(`/tasks/calendar?date=${date}`),
    staleTime: 30_000,
  });
}

export function useTasksUpcoming() {
  return useQuery({
    queryKey: ['tasks', 'upcoming'],
    queryFn: () => apiGet<{ tasks: TaskDto[] }>('/tasks/upcoming'),
    staleTime: 30_000,
  });
}

export function useTasksWaiting() {
  return useQuery({
    queryKey: ['tasks', 'waiting'],
    queryFn: () => apiGet<{ tasks: TaskDto[] }>('/tasks/waiting'),
    staleTime: 30_000,
  });
}

export type TasksScope = 'open' | 'all' | 'archived' | 'done';
export type TasksSort = 'due' | 'created' | 'updated' | 'priority';

export function useTasksAll(
  scope: TasksScope = 'open',
  sort: TasksSort = 'priority',
  q = '',
) {
  const trimmed = q.trim();
  return useQuery({
    queryKey: ['tasks', 'all', scope, sort, trimmed],
    queryFn: () => {
      const params = new URLSearchParams({ scope, sort });
      // Only send `q` once it's meaningful — the server requires ≥2 chars and
      // ignores anything shorter, so we mirror that guard client-side.
      if (trimmed.length >= 2) params.set('q', trimmed);
      return apiGet<{ tasks: TaskDto[] }>(`/tasks?${params.toString()}`);
    },
    staleTime: 30_000,
  });
}

/**
 * Global search palette query. Fetches `/tasks?q=…` scoped by `scope`, letting
 * the server default the ordering to last-updated (recency) for the query.
 * Enabled only once the term reaches the 2-char minimum the API enforces.
 */
export function useTasksSearch(q: string, scope: TasksScope = 'open') {
  const trimmed = q.trim();
  const enabled = trimmed.length >= 2;
  return useQuery({
    queryKey: ['tasks', 'search', scope, trimmed],
    enabled,
    queryFn: () => {
      const params = new URLSearchParams({ scope, q: trimmed });
      return apiGet<{ tasks: TaskDto[] }>(`/tasks?${params.toString()}`);
    },
    staleTime: 10_000,
  });
}

export function useTask(id: string | undefined) {
  return useQuery({
    queryKey: ['task', id],
    enabled: !!id,
    queryFn: () =>
      apiGet<{ task: TaskDto; tags: TaskTagDto[]; events: TaskEventDto[] }>(`/tasks/${id}`),
    staleTime: 10_000,
  });
}

export type TaskPatch = Partial<{
  title: string;
  description: string | null;
  status: TaskDto['status'];
  priority: TaskDto['priority'];
  contextId: string | null;
  projectId: string | null;
  areaId: string | null;
  personId: string | null;
  /** Setting `ownerId` implicitly clears `ownerAgentId` server-side. */
  ownerId: string | null;
  /** Setting `ownerAgentId` implicitly clears `ownerId` server-side. */
  ownerAgentId: string | null;
  dueAt: string | null;
  scheduledAt: string | null;
  reviewAt: string | null;
  waitingFor: string | null;
}>;

export type TaskCreate = Partial<Omit<TaskPatch, 'title'>> & { title: string };

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TaskCreate) => apiSend<{ task: TaskDto }>('POST', '/tasks', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function usePatchTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; patch: TaskPatch }) =>
      apiSend<{ task: TaskDto }>('PATCH', `/tasks/${vars.id}`, vars.patch),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['task', vars.id] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['inbox'] });
    },
  });
}

export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiSend<{ task: TaskDto }>('POST', `/tasks/${id}/complete`),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['task', id] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

/**
 * Toggle a task's done state.
 *
 * Open → done   uses POST /tasks/:id/complete (emits a `completed` event).
 * Done → next   uses PATCH /tasks/:id { status: 'next' } (emits
 *               `status_changed`); the server's normaliser also clears
 *               `completedAt` so the row leaves "completed today" rollups.
 *
 * Both paths apply an **optimistic update** to every `['tasks', ...]` list
 * cache so the row moves between groups (e.g. Today's Due → Closed) before
 * the server responds — without this the auto-animate transition only
 * fires after the round-trip and the UI feels laggy.
 */
export function useToggleTaskDone() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (task: Pick<TaskDto, 'id' | 'status'>) => {
      if (task.status === 'done') {
        return apiSend<{ task: TaskDto }>('PATCH', `/tasks/${task.id}`, { status: 'next' });
      }
      return apiSend<{ task: TaskDto }>('POST', `/tasks/${task.id}/complete`);
    },
    onMutate: async (task) => {
      // Cancel in-flight refetches for any task list so they don't clobber
      // our optimistic edit between our setQueriesData call and onSettled.
      await qc.cancelQueries({ queryKey: ['tasks'] });
      await qc.cancelQueries({ queryKey: ['task', task.id] });

      const nextStatus: TaskDto['status'] = task.status === 'done' ? 'next' : 'done';
      const nowIso = new Date().toISOString();

      const updater = (row: TaskDto): TaskDto =>
        row.id === task.id
          ? {
              ...row,
              status: nextStatus,
              completedAt: nextStatus === 'done' ? nowIso : null,
            }
          : row;

      // Every list endpoint returns the same shape: { tasks: TaskDto[] }.
      // Walk all of them so the row stays in sync regardless of which screen
      // the user is on when they click.
      qc.setQueriesData<{ tasks: TaskDto[] }>({ queryKey: ['tasks'] }, (data) => {
        if (!data || !Array.isArray(data.tasks)) return data as never;
        return { ...data, tasks: data.tasks.map(updater) };
      });

      // Task detail caches the single task shape.
      qc.setQueryData<{ task: TaskDto; tags: TaskTagDto[]; events: TaskEventDto[] } | undefined>(
        ['task', task.id],
        (data) => {
          if (!data) return data;
          return { ...data, task: updater(data.task) };
        },
      );
    },
    onSettled: (_data, _err, task) => {
      // Re-sync against the server; counts in particular need a real refetch.
      qc.invalidateQueries({ queryKey: ['task', task.id] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useCommentOnTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { id: string; body: string }) =>
      apiSend<{ event: TaskEventDto }>('POST', `/tasks/${vars.id}/comment`, {
        body: vars.body,
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['task', vars.id] });
    },
  });
}
